import { showDate, freeTicketNote, includedSpectatorTickets, paidSpectatorTickets } from "./ShowPieces";

const divisionLabels = {
  concours: "Concours",
  street_race: "Street/Race",
  display: "Display",
  judged: "Judged",
};

function typeLabel(event, code) {
  const match = (event?.entry_options || []).find((item) => item.code === code);
  if (match) {
    return match.title;
  }
  return divisionLabels[code] || code || "Display";
}

function dollars(value) {
  const amount = Number(value);
  if (Number.isNaN(amount)) {
    return "";
  }
  return amount.toLocaleString("en-US", { style: "currency", currency: "USD" });
}

function EntryReceipt({
  event,
  userName,
  userEmail,
  cars,
  garage,
  tickets,
  spectators = 0,
  lunch,
  onAddCar,
  onEdit,
  onRemove,
  onTicketQty,
  onSpectatorQty,
}) {
  const fee = Number(event?.entry_fee || 0);
  const entries = (cars || []).map((item) => {
    const owned = (garage || []).find((car) => String(car.id) === String(item.vehicle));
    const photo = owned?.images?.[0];
    return {
      ...item,
      photo: photo?.display || photo?.thumb || "",
      color: owned?.exterior_color || "",
    };
  });
  const ticketLines = Object.entries(tickets || {})
    .filter(([, quantity]) => Number(quantity) > 0)
    .map(([id, quantity]) => {
      const ticket = lunch?.ticket_types?.find((item) => String(item.id) === String(id));
      const qty = Number(quantity);
      const price = Number(ticket?.price || 0);
      return {
        id,
        name: ticket?.name || "VIP ticket",
        qty,
        price,
        total: qty * price,
      };
    });
  const entryTotal = entries.length * fee;
  const spectatorQty = Number(spectators) || 0;
  const vipHeld = ticketLines.reduce((sum, line) => sum + line.qty, 0);
  const atVipCap = vipHeld >= spectatorQty;
  const includedTickets = includedSpectatorTickets(entries.length);
  const paidTickets = paidSpectatorTickets(entries.length, spectatorQty);
  const spectatorPrice = Number(event?.spectator_fee || 0);
  const spectatorTotal = paidTickets * spectatorPrice;
  const freeNote = freeTicketNote(entries.length);
  const lunchTotal = ticketLines.reduce((sum, line) => sum + line.total, 0);

  return (
    <>
      <h2 className="entry-step-title">Your receipt</h2>
      <div className="receipt">
        {entries.length === 0 ? (
          <p className="quiet">No cars are on this receipt yet.</p>
        ) : (
          <div className="admit-list">
            {entries.map((item) => (
              <article key={`${item.vehicle}-${item.event_car_class}`} className="admit-ticket is-entry">
                <div className={item.photo ? "admit-stub is-photo" : "admit-stub"}>
                  {item.photo ? (
                    <img src={item.photo} alt="" />
                  ) : (
                    <span className="admit-mark">Class</span>
                  )}
                </div>
                <div className="admit-main">
                  <div className="receipt-kicker-row">
                    <p className="site-kicker">Car entry</p>
                    <div className="receipt-entry-actions">
                      <button
                        type="button"
                        aria-label={`Edit ${item.label || item.class_title || "this car"}`}
                        title={`Edit ${item.label || item.class_title || "this car"}`}
                        onClick={() => onEdit(item)}
                      >
                        <svg className="receipt-edit-icon" viewBox="0 0 16 16" aria-hidden="true">
                          <path
                            d="M11.2 2.4l2.4 2.4-7.6 7.6H3.6V10z"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="1.3"
                            strokeLinejoin="round"
                          />
                          <path d="M9.6 4l2.4 2.4" fill="none" stroke="currentColor" strokeWidth="1.3" />
                        </svg>
                        Edit
                      </button>
                      <span aria-hidden="true">|</span>
                      <button
                        type="button"
                        className="receipt-remove"
                        aria-label={`Remove ${item.label || item.class_title || "this car"}`}
                        title={`Remove ${item.label || item.class_title || "this car"}`}
                        onClick={() => onRemove(item)}
                      >
                        X Remove
                      </button>
                    </div>
                  </div>
                  <div className="receipt-title-row">
                    <h3>{item.class_title}</h3>
                    <span className="receipt-dash" aria-hidden="true">
                      -
                    </span>
                    <p className="admit-host">
                      {item.label}
                      {item.color ? ` · ${item.color}` : ""}
                    </p>
                  </div>
                  <div className="admit-fare">
                    <p className="receipt-meta">
                      <strong>{typeLabel(event, item.division)}</strong>
                      <span className="receipt-bar" aria-hidden="true">
                        |
                      </span>
                      <span className={item.judging ? "receipt-yes" : "receipt-no"}>
                        Judging {item.judging ? "Yes" : "No"}
                      </span>
                    </p>
                    <strong className="receipt-amount">{dollars(fee)}</strong>
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}
        <div className="receipt-add">
          <button type="button" className="btn btn-register-another" onClick={onAddCar}>
            {entries.length === 0 ? "Register a car" : "Register another car"}
          </button>
        </div>
        {spectatorQty > 0 ? (
          <div className="admit-list">
            <article className="admit-ticket">
              <div className="admit-stub">
                <span className="admit-mark">Show</span>
              </div>
              <div className="admit-main">
                <p className="site-kicker">Spectator admission</p>
                <h3>{event?.title}</h3>
                <div className="receipt-ticket-line">
                  <p className="admit-host">{spectatorQty} × Spectator ticket</p>
                  <div className="admit-qty">
                    <button
                      type="button"
                      aria-label={
                        spectatorQty <= includedTickets
                          ? "Included tickets stay with the registered cars"
                          : "Fewer spectator tickets"
                      }
                      title={
                        spectatorQty <= includedTickets
                          ? "Included tickets stay with the registered cars"
                          : "Fewer spectator tickets"
                      }
                      disabled={spectatorQty <= includedTickets}
                      onClick={() => onSpectatorQty(spectatorQty - 1)}
                    >
                      −
                    </button>
                    <button
                      type="button"
                      aria-label="More spectator tickets"
                      title="More spectator tickets"
                      onClick={() => onSpectatorQty(spectatorQty + 1)}
                    >
                      +
                    </button>
                  </div>
                </div>
                <div className="admit-fare">
                  <div>
                    <strong>
                      {spectatorPrice > 0 ? `${dollars(spectatorPrice)} each` : "Price will be posted here."}
                      {freeNote ? <span className="ticket-free-note"> {freeNote}</span> : null}
                    </strong>
                  </div>
                  <strong className="receipt-amount">{dollars(spectatorTotal)}</strong>
                </div>
              </div>
            </article>
          </div>
        ) : null}
        {ticketLines.length > 0 ? (
          <div className="admit-list">
            {atVipCap ? (
              <p className="spectator-lead">
                {spectatorQty < 1
                  ? "Add a spectator ticket before a VIP luncheon ticket."
                  : `VIP tickets match your ${spectatorQty} spectator ${spectatorQty === 1 ? "ticket" : "tickets"}.`}
              </p>
            ) : null}
            {ticketLines.map((line) => (
              <article key={line.id} className="admit-ticket">
                <div className="admit-stub">
                  {lunch?.logo_url ? (
                    <img className="admit-logo" src={lunch.logo_url} alt="" />
                  ) : (
                    <span className="admit-mark">VIP</span>
                  )}
                </div>
                <div className="admit-main">
                  <p className="site-kicker">VIP luncheon</p>
                  <h3>{lunch?.title || "VIP luncheon"}</h3>
                  <div className="receipt-ticket-line">
                    <p className="admit-host">
                      {line.qty} × {line.name}
                    </p>
                    <div className="admit-qty">
                      <button
                        type="button"
                        aria-label={`Fewer ${line.name} tickets`}
                        title={`Fewer ${line.name} tickets`}
                        onClick={() => onTicketQty(line.id, line.qty - 1)}
                      >
                        −
                      </button>
                      <button
                        type="button"
                        aria-label={
                          spectatorQty < 1
                            ? "Add a spectator ticket before a VIP ticket"
                            : atVipCap
                              ? "VIP tickets match the spectator tickets"
                              : `More ${line.name} tickets`
                        }
                        title={
                          spectatorQty < 1
                            ? "Add a spectator ticket before a VIP ticket"
                            : atVipCap
                              ? "VIP tickets match the spectator tickets"
                              : `More ${line.name} tickets`
                        }
                        disabled={spectatorQty < 1 || atVipCap}
                        onClick={() => onTicketQty(line.id, line.qty + 1)}
                      >
                        +
                      </button>
                    </div>
                  </div>
                  <div className="admit-fare">
                    <div>
                      <strong>{dollars(line.price)} each</strong>
                    </div>
                    <strong className="receipt-amount">{dollars(line.total)}</strong>
                  </div>
                </div>
              </article>
            ))}
          </div>
        ) : null}

        <div className="receipt-foot">
          <div className="receipt-who">
            {userName ? <p className="receipt-name">{userName}</p> : null}
            {userEmail ? <p>{userEmail}</p> : null}
            <p>{event?.title}</p>
            <p>{showDate(event?.date)}</p>
            <span className="receipt-status">Draft</span>
          </div>
          <dl className="receipt-totals">
            <div>
              <dt>Entry</dt>
              <dd>{dollars(entryTotal)}</dd>
            </div>
            {spectatorQty > 0 ? (
              <div>
                <dt>Spectators</dt>
                <dd>{dollars(spectatorTotal)}</dd>
              </div>
            ) : null}
            {ticketLines.length > 0 ? (
              <div>
                <dt>Luncheon</dt>
                <dd>{dollars(lunchTotal)}</dd>
              </div>
            ) : null}
            <div className="receipt-grand">
              <dt>Total</dt>
              <dd>{dollars(entryTotal + spectatorTotal + lunchTotal)}</dd>
            </div>
          </dl>
        </div>
      </div>
    </>
  );
}

export default EntryReceipt;
